import React, { useEffect, useState, useRef, Component } from 'react';
import { TransformWrapper, TransformComponent, useControls, useTransformContext, type ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { ZoomInIcon, ZoomOutIcon, ScanIcon } from 'lucide-react';
// A4 width at 96dpi (210mm).
const A4_WIDTH_PX = 794;
/** Floating control cluster: zoom out · live % · zoom in · fit · 100%. */
function Controls({
  onFit,
  onReset
}: {
  onFit: () => void;
  onReset: () => void;
}) {
  const {
    zoomIn,
    zoomOut
  } = useControls();
  const ctx = useTransformContext();
  const [pct, setPct] = useState(100);
  // Poll the live scale so the % readout stays in sync during gestures.
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      const s = ctx.transformState?.scale ?? 1;
      setPct(Math.round(s * 100));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [ctx]);
  const icon = 'flex h-9 w-9 items-center justify-center text-[var(--ink-soft)] transition-colors hover:text-[var(--ink)] focus:outline-none focus-visible:text-[var(--accent)] active:scale-90 disabled:opacity-40';
  return <div className="no-print pointer-events-auto fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-1 rounded-full border border-[var(--line-strong)] bg-white/95 px-1.5 py-1 shadow-lg backdrop-blur" data-id="element-181" data-mp="4rcsld6n2xap">
      <button type="button" aria-label="Zoom out" className={icon} onClick={() => zoomOut()} data-id="element-182" data-mp="drhux8wz75i0">
        <ZoomOutIcon className="h-4 w-4" data-id="element-183" data-mp="9pwc76xrfwzw" />
      </button>

      <button type="button" onClick={onReset} aria-label="Reset to 100%" className="min-w-[52px] rounded-full px-2 py-1 text-center font-mono text-[11px] tabular-nums text-[var(--ink)] transition-colors hover:bg-[var(--line)]" data-id="element-184" data-mp="nqqnpyg3ue2s">
        {pct}%
      </button>

      <button type="button" aria-label="Zoom in" className={icon} onClick={() => zoomIn()} data-id="element-185" data-mp="2kj0kuvapk1n6">
        <ZoomInIcon className="h-4 w-4" data-id="element-186" data-mp="ognq6tsxlp50" />
      </button>

      <span className="mx-0.5 h-5 w-px bg-[var(--line-strong)]" aria-hidden="true" data-id="element-187" data-mp="te5qxwwsoy1x" />

      <button type="button" aria-label="Fit to width" className={icon} onClick={onFit} data-id="element-188" data-mp="xfixk97pp59w">
        <ScanIcon className="h-4 w-4" data-id="element-189" data-mp="12g6pcx3rlq56" />
      </button>
    </div>;
}
/**
 * Figma-like canvas for the fixed-width A4 CV: fits to the viewport width, then
 * supports pinch/wheel zoom, drag-pan (with grab cursors) and double-tap zoom.
 * Print bypasses all transforms (handled in @media print).
 */
export function ScaledSheet({
  children
}: {
  children: React.ReactNode;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<ReactZoomPanPinchRef | null>(null);
  const [fitScale, setFitScale] = useState(1);
  const [canvasH, setCanvasH] = useState<number>();
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    const compute = () => {
      const w = wrapRef.current?.clientWidth ?? A4_WIDTH_PX;
      const scale = Math.min(1, (w - 8) / A4_WIDTH_PX);
      setFitScale(scale);
      // Canvas height = the fitted height of the content, so no space is
      // wasted below the last page. Capped to the viewport so tall CVs still
      // pan rather than pushing the page down forever.
      const naturalH = contentRef.current?.offsetHeight ?? 0;
      if (naturalH > 0) {
        const fitted = naturalH * scale;
        const cap = window.innerHeight * 0.85;
        setCanvasH(Math.min(fitted, cap) + 16);
      }
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (wrapRef.current) ro.observe(wrapRef.current);
    if (contentRef.current) ro.observe(contentRef.current);
    window.addEventListener('resize', compute);
    const t = window.setTimeout(compute, 600);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', compute);
      window.clearTimeout(t);
    };
  }, []);
  // Position the page fitted to width and aligned to the TOP of the canvas
  // (a multi-page CV should start at page 1, not vertically centred).
  const alignTop = (scale: number, ms: number) => {
    const api = apiRef.current;
    const canvasW = wrapRef.current?.clientWidth ?? A4_WIDTH_PX;
    if (!api) return;
    const x = (canvasW - A4_WIDTH_PX * scale) / 2; // horizontally centred
    api.setTransform(Math.max(0, x), 16, scale, ms);
  };
  useEffect(() => {
    alignTop(fitScale, 0);
  }, [fitScale]);
  const fit = () => alignTop(fitScale, 250);
  const reset = () => alignTop(1, 250);
  return <div ref={wrapRef} style={{
    height: canvasH
  }} className={`cv-canvas relative w-full overflow-hidden ${dragging ? 'cursor-grabbing' : 'cursor-grab'}`} data-id="element-190" data-mp="2wddelj1azk5g">
      <TransformWrapper ref={apiRef} initialScale={fitScale} minScale={Math.min(fitScale * 0.4, 0.25)} maxScale={3} limitToBounds={false} doubleClick={{
      mode: 'toggle',
      step: 1.4
    }} wheel={{
      step: 0.12
    }} pinch={{
      step: 6
    }} panning={{
      velocityDisabled: true
    }} onPanningStart={() => setDragging(true)} onPanningStop={() => setDragging(false)} data-id="element-191" data-mp="3o4j6xdas8dk6">
        <Controls onFit={fit} onReset={reset} data-id="element-192" data-mp="1m9vipyaszzu5" />
        <TransformComponent wrapperStyle={{
        width: '100%',
        height: '100%'
      }} contentStyle={{
        width: A4_WIDTH_PX,
        cursor: 'inherit'
      }} data-id="element-193" data-mp="yhenem222bcw">
          <div ref={contentRef} data-id="element-194" data-mp="18hysy0wly20x">{children}</div>
        </TransformComponent>
      </TransformWrapper>
    </div>;
}